import { ApiResponse, ValidationError } from '../../../types/Response'
import { exists } from '../../../UtilityHelpers'

/**
 * Deep compares two ValidationError objects for equality.
 */
const areValidationErrorsEqual = (a: ValidationError, b: ValidationError): boolean => {
    return (
        a.propertyName === b.propertyName &&
        a.errorCode === b.errorCode &&
        a.errorMessage === b.errorMessage &&
        a.severity === b.severity &&
        JSON.stringify(a.data) === JSON.stringify(b.data)
    )
}

/**
 * Removes duplicate ValidationErrors using deep comparison.
 */
const deduplicateValidationErrors = (errors: ValidationError[]): ValidationError[] => {
    return errors.reduce<ValidationError[]>((unique, error) => {
        const isDuplicate = unique.some((existing) => areValidationErrorsEqual(existing, error))
        return isDuplicate ? unique : [...unique, error]
    }, [])
}

/**
 * Merges validationErrors arrays with priority: array with items > empty array > null/undefined.
 * Duplicates are removed using deep comparison.
 */
const mergeValidationErrors = (
    current: ValidationError[] | null | undefined,
    incoming: ValidationError[] | null | undefined,
): ValidationError[] | null | undefined => {
    const currentExists = exists(current)
    const incomingExists = exists(incoming)

    // Neither exists
    if (!currentExists && !incomingExists) {
        return undefined
    }

    // Only one exists - prefer the one that exists
    if (!currentExists && incomingExists) {
        return deduplicateValidationErrors(incoming)
    }
    if (currentExists && !incomingExists) {
        return deduplicateValidationErrors(current)
    }

    // Both exist - merge and deduplicate
    const merged = [...current!, ...incoming!]
    return deduplicateValidationErrors(merged)
}

/**
 * Merges isSuccess with priority: false wins over true (pessimistic merge).
 */
const mergeIsSuccess = (current: boolean | undefined, incoming: boolean | undefined): boolean | undefined => {
    if (current === false || incoming === false) {
        return false
    }
    if (current === true || incoming === true) {
        return true
    }
    return undefined
}

/**
 * Merges multiple ApiResponse objects with property-level priority:
 * - `isSuccess`: `false` has priority over `true`
 * - `validationErrors`: array with items > empty array > null/undefined; duplicates removed via deep compare
 * - `errorCode`, `message`, `data`: last non-null/undefined value wins
 */
export const mergeApiResponses = async <TApiResponse extends ApiResponse>(
    apiResponsesToMerge: TApiResponse[],
): Promise<TApiResponse> => {
    return apiResponsesToMerge.reduce<TApiResponse>((acc, curr) => {
        return {
            ...acc,
            ...curr,
            // Override with priority-based merging for specific properties
            isSuccess: mergeIsSuccess(acc.isSuccess, curr.isSuccess),
            validationErrors: mergeValidationErrors(acc.validationErrors, curr.validationErrors),
            // For errorCode, message, data: keep current if incoming is null/undefined
            errorCode: exists(curr.errorCode) ? curr.errorCode : acc.errorCode,
            message: exists(curr.message) ? curr.message : acc.message,
            data: exists(curr.data) ? curr.data : acc.data,
        } as TApiResponse
    }, {} as TApiResponse)
}
